import { useAuth } from 'lib/context';
import { pushToDataLayer } from 'lib/hooks/GTM/pushToDataLayer';
import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CovideoGTMVideoAttributes,
  GTMCDKUserAttributes,
  GTMEvent,
  GTMPageApplication,
  GTMPageviewAttributes,
  GTMVehicleAttributes,
  GTMVideoAttributes,
} from './types';
import { mapToGtmVehicleAttributes, mapToGTMVideoAttributes } from './utils';
import { useGtmVinInventoryQuery } from 'lib/api/inventoryItem/useGtmVinInventoryQuery';
import { mapToGTMMeetingAttributes } from './actions/meeting/utils';
import {
  CovideoGTMMeetingAttributes,
  GTMMeetingAttributes,
} from './actions/meeting/types';
import { useCDK } from '../useCdk';

// partial/contains match for these
const STATIC_EXCLUDES = [
  '/send-and-share',
  '/classic/edit-video',
  '/voiceover',
  '/record',
  '/merge',
];

// exactly: /inventory/<one-segment> (not /inventory or /inventory/a/b)
const INVENTORY_ITEM_RX = /^\/inventory\/[^/]+(?:\/|$)/;
// exactly: /inventory/<one-segment> (not /inventory or /inventory/a/b)
const MEETINGS_ITEM_RX = /^\/meetings\/[^/]+(?:\/|$)/;

const PAGE_SECTION_MAP: Record<string, string> = {
  users: 'My Team',
  overview: 'My Stats',
  custom: 'Custom Reports',
  'activity-feed': 'Activity Feed',
  'landing-pages': 'Landing Pages',
  'service-pages': 'Sales Pages',
  'calls-to-action': 'Call-to-Actions',
  profile_edituserinfo: 'My Profile',
  profile_editcompanyinfo: 'Company Profile',
  profile_videoattributes: 'Custom Video Attributes',
  teleprompter: 'Teleprompter Scripts',
  'recently-deleted': 'Recently Deleted',
  details: 'Manage',
  trim: 'Trimming',
  home: 'Overview',
  meetings: 'Overview',
  guides: '',
  customers: '',
  inventory: 'Overview',
};

const PAGE_LOCATION_MAP: Record<string, string> = {
  classic: 'Library',
  home: 'Videos',
  guides: 'Training',
};

const capitalizeFirst = (str?: string): string | undefined =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : undefined;

const mapPageSection = (section: string | undefined): string | undefined => {
  if (!section) {
    return undefined;
  }
  // skip IDs
  if (!isNaN(Number(section))) {
    return undefined;
  }
  const key = section.toLowerCase();

  return PAGE_SECTION_MAP[key] === ''
    ? undefined
    : PAGE_SECTION_MAP[key] || capitalizeFirst(section);
};

const mapPageLocation = (location: string | undefined): string | undefined => {
  if (!location) {
    return undefined;
  }
  const key = location.toLowerCase();
  return PAGE_LOCATION_MAP[key] || capitalizeFirst(location);
};

export function useGTMTrackPageView({ vin }: { vin: string }) {
  const { data, isSuccess: vehiceReady } = useGtmVinInventoryQuery({
    vin,
  });

  const { userData } = useAuth();
  const { isCdkUser } = useCDK();
  const location = useLocation();
  const { pathname } = location;

  const segments = pathname.split('/').filter(Boolean);

  const autoLocationSlug = segments?.[0];

  // Last segment → leaf, but skip if it's numeric (e.g. an ID)
  let autoSectionSlug = segments?.at(-1);

  if (autoSectionSlug && !isNaN(Number(autoSectionSlug))) {
    // If the last segment is a number, use the previous one instead
    autoSectionSlug = segments?.at(-2);
  }

  // Base attributes
  const autoPageSection = mapPageSection(autoSectionSlug);
  const autoPageLocation = mapPageLocation(autoLocationSlug);

  // if vin is empty, skip vehicle data do not use cached data
  const item = vin ? data?.items?.[0] : null;

  const cdkUserAttributes: GTMCDKUserAttributes = {
    user_id: userData.email,
    enterprise_id: userData.customer.cdkEnterpriseId || undefined,
    store_id: userData.customer.cdkStoreId || undefined,
    store_name: userData.customer.business || undefined,
    is_internal_user: userData?.email?.includes('cdk'),
  };
  const vehicleData: GTMVehicleAttributes = mapToGtmVehicleAttributes(item);

  // actions
  const trackVideoSpecificPagesManually = useCallback(
    ({
      pageLocation,
      pageSection,
      videoAttributes,
      meetingAttributes,
      excludeVehicleData = false,
      vehicle,
      mergeIds,
    }: {
      pageLocation?: string;
      pageSection?: string;
      videoAttributes?: CovideoGTMVideoAttributes;
      meetingAttributes?: CovideoGTMMeetingAttributes;
      excludeVehicleData?: boolean;
      vehicle?: GTMVehicleAttributes;
      mergeIds?: number[];
    }) => {
      const gtmMeetingAttributes: GTMMeetingAttributes =
        mapToGTMMeetingAttributes(meetingAttributes);

      const gtmVideoAttributes: GTMVideoAttributes =
        mapToGTMVideoAttributes(videoAttributes);

      const pageViewAttributes: GTMPageviewAttributes = {
        event: GTMEvent.COVIDEO_PAGEVIEW,
        page_application: GTMPageApplication.CDK_VIDEO_WHITELABEL,
        page_url: window.location.href,
        page_location: pageLocation || autoPageLocation || '',
        page_section: pageSection || autoPageSection || undefined,
      };

      const payload = {
        ...pageViewAttributes,
        ...cdkUserAttributes,
        ...gtmVideoAttributes,
        ...(excludeVehicleData ? {} : vehicleData),
        ...vehicle,
        ...gtmMeetingAttributes,
        ...(mergeIds ? { merge_ids: mergeIds } : {}),
      };

      pushToDataLayer(payload);
    },
    [location.pathname, userData, vehicleData.vin]
  );

  const trackPageViewAutomatically = useCallback(() => {
    const pageViewAttributes: GTMPageviewAttributes = {
      event: GTMEvent.COVIDEO_PAGEVIEW,
      page_application: GTMPageApplication.CDK_VIDEO_WHITELABEL,
      page_url: window.location.href,
      page_location: autoPageLocation || '',
      page_section: autoPageSection || '',
    };

    const payload = {
      ...pageViewAttributes,
      ...cdkUserAttributes,
    };

    pushToDataLayer(payload);
  }, [location.pathname, userData]);

  useEffect(() => {
    if (!userData) {
      return;
    }

    if (!isCdkUser) {
      return;
    }

    // Skip auto-track if path is in exclusion list
    const isExcluded =
      STATIC_EXCLUDES.some(s => pathname.includes(s)) ||
      INVENTORY_ITEM_RX.test(pathname) ||
      MEETINGS_ITEM_RX.test(pathname);

    if (isExcluded) {
      return;
    }

    trackPageViewAutomatically();
  }, [location.pathname, userData, trackVideoSpecificPagesManually]);

  return {
    trackVideoSpecificPagesManually: isCdkUser
      ? trackVideoSpecificPagesManually
      : undefined,
    vehiceReady: vehiceReady,
  };
}
